import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { DefaultCatchBoundary } from './components/DefaultCatchBoundary';
import { NotFound } from './components/NotFound';
import { routeTree } from './routeTree.gen';
import { getAppContext } from './utils/app-context';

function trimTrailingSlash(path: string) {
	return path.replace(/\/$/, '');
}

// You must export a getRouter function that returns a new router instance each time
export function getRouter() {
	// default context. this context will be updated by server.ts before rendering starts on server side

	const router = createTanStackRouter({
		routeTree,
		basepath: '/',
		defaultPreload: 'intent',
		// TODO: handle error. use defaultOnCatch/onError to handle error
		defaultViewTransition: {
			types: ({ fromLocation, toLocation, pathChanged }) => {
				// Skip view transitions entirely for same-path navigations
				// (hash-only changes from sheet open/close, search param
				// updates that `pathChanged` flags as false-positive). Returning
				// `false` short-circuits `document.startViewTransition` inside
				// tanstack — important on Safari, where even an empty-types
				// transition runs a default root crossfade that flickers against
				// sheet close animations.
				if (
					!pathChanged ||
					!fromLocation ||
					trimTrailingSlash(fromLocation.pathname) ===
						trimTrailingSlash(toLocation.pathname)
				) {
					return false;
				}
				// TEMP: no slide transition into the CBCC apply page — the slide
				// looked wrong on that redirect. Scoped to apply only; other routes
				// keep the slide. Revisit when the page transitions are unified.
				if (
					trimTrailingSlash(toLocation.pathname) === '/travel-credit-card/apply'
				) {
					return false;
				}
				let direction = 'none';

				if (fromLocation) {
					const fromIndex = fromLocation.state.__TSR_index;
					const toIndex = toLocation.state.__TSR_index;

					direction = fromIndex > toIndex ? 'right' : 'left';
				}

				return [`slide-${direction}`];
			},
		},
		defaultErrorComponent: DefaultCatchBoundary,
		defaultNotFoundComponent: () => <NotFound />,
		// on client side context will be initialized by client.tsx
		// we may explore createIsmoMorphicFn in future to handle in this file itself
		context: getAppContext(),
	});

	return router;
}

declare module '@tanstack/react-router' {
	interface Register {
		router: ReturnType<typeof getRouter>;
	}
}
